/**
 * The CAODC working-day certification counter, in one place.
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * Six screens rendered this figure and no two agreed. Four spelled it
 * "{n} / {m} working days" meaning days USED; one spelled it "{n} of {m}
 * working days" meaning days REMAINING; and EquipmentCertScreen rendered BOTH
 * readings on one line — "12 / 30 working days" beside "18 days remaining" —
 * with nothing on screen telling a reader which number was which.
 *
 * That is not a cosmetic inconsistency. It is a safety budget: 4 of 30 and
 * 4 remaining of 30 are opposite facts about whether a tool may go out.
 *
 * ─── ONE PHRASING: USED-OF-LIMIT ────────────────────────────────────────────
 *
 * `{used} of {limit} working days` — used, not remaining, is the default.
 *
 * It matches the server's own vocabulary. The column is
 * `working_days_since_inspection` and its ceiling is `cert_day_limit`; the
 * cert-warning payload the assign endpoint returns carries exactly those two
 * names and no remaining-shaped field. A screen that says "remaining" is
 * translating, and every translation is a place the two can disagree.
 *
 * It also matches the bar. Every one of these counters sits next to a progress
 * bar that fills as the budget is consumed, and a bar at 90% beside the words
 * "50 working days" reads as a contradiction unless the 50 is what is used.
 *
 * ─── THE ONE PLACE THAT SAYS REMAINING, AND WHY IT KEEPS SAYING IT ──────────
 *
 * `mode: 'remaining'` exists for the inspection record — a filed document, not
 * a live budget bar. Two reasons it is a mode rather than a site to convert:
 *
 *   1. Its own label already says it. The row reads
 *      "Cert days remaining: 496 of 500 working days". The label disambiguates
 *      the sentence in a way none of the bar sites do.
 *   2. THE API PRINTS THE IDENTICAL STRING. inspection-templates.routes.ts
 *      renders `${cert_days_remaining} of ${cert_day_limit} working days` into
 *      the PDF header. Converting the screen to used-of-limit would make the
 *      screen and its own PDF disagree about a number — one answer on paper,
 *      another on glass. Changing both is an API change, not a frontend one.
 *
 * So: `used` everywhere a bar is involved, `remaining` on the inspection
 * record, and the mode is explicit at every call site so nobody has to guess.
 *
 * ─── ABSENT IS NOT ZERO ─────────────────────────────────────────────────────
 *
 * A tool with no `working_days_since_inspection` is UNTRACKED. Rendering it as
 * `0 of 500` says "just inspected, full budget ahead" — the exact inverse of
 * what is known, on the number that decides whether gear goes down a hole.
 * Three of the six sites did that. Absent renders `—`.
 *
 * A LIMIT OF ZERO IS ALSO ABSENT. `cert_day_limit: 0` is how the API spells
 * "no cert tracking" on rows the old editor touched, and `0 of 0 working days`
 * at 0% reads as a configured budget. Guarded here rather than at five call
 * sites, two of which already carried a comment about the stray zero.
 *
 * ─── WHAT THIS DOES NOT DO ──────────────────────────────────────────────────
 *
 * It does not derive the counter. Two of the four endpoints behind these
 * screens flatten NULL to 0 server-side before the frontend ever sees it
 * (`COALESCE(working_days_since_inspection, 0)` in GET /equipment/cert-summary,
 * and `workingDays ?? sumDaysUsed` in getEquipmentCertUsage). On those two
 * surfaces an untracked tool still arrives as a 0 that is indistinguishable
 * from a real one, and no client-side rule can tell them apart without
 * inventing a second answer to the question. Reported for the API, not worked
 * around here.
 */

/** How the sentence reads. Always passed explicitly — never defaulted at a call site. */
export type CertDayMode = 'used' | 'remaining';

export interface CertDayInput {
  /** `working_days_since_inspection`. NULL/undefined means untracked, not zero. */
  daysUsed: number | null | undefined;
  /** `cert_day_limit`. NULL/undefined/0 all mean "no cert budget configured". */
  dayLimit: number | null | undefined;
  /**
   * The API's own `cert_days_remaining`, where the payload carries one.
   *
   * Only the inspection-record endpoint sends this, and it is passed through
   * rather than derived because deriving it would be a second answer to a
   * question the server already answers — the same figure is computed in SQL
   * (`cert_day_limit - COALESCE(working_days_since_inspection, 0)`) and printed
   * into the PDF from there. Omit it and remaining mode falls back to
   * limit − used, which is what a payload without the field means anyway.
   */
  daysRemaining?: number | null;
}

/** A limit of 0 means unconfigured, not a budget of nothing. */
function usableLimit(dayLimit: number | null | undefined): number | null {
  return typeof dayLimit === 'number' && Number.isFinite(dayLimit) && dayLimit > 0
    ? dayLimit
    : null;
}

function usableUsed(daysUsed: number | null | undefined): number | null {
  return typeof daysUsed === 'number' && Number.isFinite(daysUsed) ? daysUsed : null;
}

/**
 * Is there enough to render a counter at all?
 *
 * Both halves are required. A used count with no limit is a number with no
 * scale, and a limit with no used count is a budget with no position in it.
 */
export function hasCertDays(input: CertDayInput): boolean {
  return usableUsed(input.daysUsed) !== null && usableLimit(input.dayLimit) !== null;
}

/** True when `formatCertDays` would produce a sentence rather than a dash. */
export function hasCertDaysFor(input: CertDayInput, mode: CertDayMode): boolean {
  return formatCertDays(input, mode) !== '—';
}

/**
 * The counter sentence, or `—`.
 *
 * NOT CLAMPED IN EITHER DIRECTION. A tool 12 days past a 30-day limit reads
 * "42 of 30 working days" in used mode and "-12 of 30 working days" in
 * remaining mode. An overrun is the case this figure exists to surface, and
 * clamping renders the worst rows as though they were merely full.
 */
export function formatCertDays(input: CertDayInput, mode: CertDayMode): string {
  const limit = usableLimit(input.dayLimit);
  if (limit === null) return '—';

  if (mode === 'remaining') {
    // The server's figure wins when it sent one.
    const remaining = usableUsed(input.daysRemaining);
    if (remaining !== null) return `${remaining} of ${limit} working days`;
    const used = usableUsed(input.daysUsed);
    if (used === null) return '—';
    return `${limit - used} of ${limit} working days`;
  }

  const used = usableUsed(input.daysUsed);
  if (used === null) return '—';
  return `${used} of ${limit} working days`;
}

/**
 * Percent of the budget consumed, or null when there is no budget.
 *
 * Rounded to a whole number and NOT capped — a tool at 140% should say so.
 * Callers that size a bar from this must clamp the WIDTH, not the number.
 *
 * Only for surfaces whose payload has no percent of its own. Where the API
 * sends one (`percentUsed` on /equipment/cert-summary, `percent_used` on
 * /employee/my-equipment/:id) pass that through instead — two answers to one
 * question is the thing this file exists to stop.
 */
export function certDayPercent(input: CertDayInput): number | null {
  const used = usableUsed(input.daysUsed);
  const limit = usableLimit(input.dayLimit);
  if (used === null || limit === null) return null;
  return Math.round((used / limit) * 100);
}

/** Bar width, clamped to the track. Separate from the percent so the number can exceed 100. */
export function certDayBarWidth(percent: number | null): string {
  if (percent === null) return '0%';
  return `${Math.max(0, Math.min(100, percent))}%`;
}

/**
 * The counter's colour, keyed on percent consumed.
 *
 * Thresholds were identical in four copies of `progressColor`/
 * `progressTextColor` (EquipmentScreen, EquipmentCertScreen,
 * FieldEquipmentDetail, EquipmentDetail) plus a fifth hand-rolled inline as
 * `days >= limit * 0.9`. The inline one disagreed with the other four across a
 * rounding boundary — 0.896 of the limit rounds to 90% and is red by the
 * shared rule, amber by the inline one.
 */
export function certDayTextColor(percent: number | null): string {
  if (percent === null) return 'text-fieldiq-muted';
  if (percent >= 90) return 'text-red-400';
  if (percent >= 75) return 'text-amber-400';
  return 'text-green-400';
}

export function certDayBarColor(percent: number | null): string {
  if (percent === null) return 'bg-fieldiq-border';
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-green-500';
}
